<template>
    <div class="tab-detail">
        <div class="header">
            <div class="title">{{ $t("googleBusiness.locations") }}</div>
            <div class="header-actions">
                <a-button type="primary" @click="syncLocations" :loading="syncing">
                    <span class="material-icons" style="font-size: 16px; margin-right: 4px;">sync</span>
                    {{ $t("googleBusiness.sync") }}
                </a-button>
                <a-button @click="fetchLocations" :loading="loading">
                    <a-icon type="reload" /> {{ $t("googleBusiness.refresh") }}
                </a-button>
            </div>
        </div>
        <div class="locations-container" v-if="!loading">
            <div class="location-card" v-for="location in locations" :key="location.locationId">
                <div class="location-icon">
                    <span class="material-icons">store</span>
                </div>
                <div class="info">
                    <h3 class="location-name">{{ location.name }}</h3>
                    <div class="location-address" v-if="location.address">
                        <span class="material-icons">location_on</span>
                        {{ location.address }}
                    </div>
                    <div class="meta">
                        <span v-if="location.phoneNumber">
                            <span class="material-icons">phone</span> {{ location.phoneNumber }}
                        </span>
                        <span v-if="location.averageRating">
                            <span class="material-icons">star</span> {{ location.averageRating.toFixed(1) }}
                        </span>
                        <span v-if="location.totalReviewCount">
                            <span class="material-icons">reviews</span> {{ location.totalReviewCount }} reviews
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <div class="loading" v-else>
            <a-spin />
        </div>
        <div class="empty" v-if="!loading && locations.length === 0">
            <a-empty :description="$t('googleBusiness.noLocationsFound')" />
        </div>
        <div class="pagination" v-if="totalPages > 1">
            <a-pagination
                :current="page"
                :total="total"
                :page-size="limit"
                @change="onPageChange"
            />
        </div>
    </div>
</template>

<script>
import { GOOGLE_BUSINESS_API } from "../../../../../api/google-business";

export default {
    name: "LocationList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            locations: [],
            loading: false,
            syncing: false,
            page: 1,
            limit: 12,
            total: 0,
            totalPages: 0,
        };
    },
    methods: {
        async fetchLocations() {
            this.loading = true;
            try {
                const response = await GOOGLE_BUSINESS_API.listLocations(this.channel._id, this.page, this.limit);
                this.locations = response.data.locations;
                this.total = response.data.total;
                this.totalPages = response.data.totalPages;
            } catch (err) {
                this.$message.error(this.$t("googleBusiness.errorFetchingLocations"));
            } finally {
                this.loading = false;
            }
        },
        async syncLocations() {
            this.syncing = true;
            try {
                await GOOGLE_BUSINESS_API.syncLocations(this.channel._id);
                this.$message.success(this.$t("googleBusiness.locationsSynced"));
                await this.fetchLocations();
            } catch (err) {
                this.$message.error(this.$t("googleBusiness.errorSyncingLocations"));
            } finally {
                this.syncing = false;
            }
        },
        onPageChange(page) {
            this.page = page;
            this.fetchLocations();
        },
    },
    mounted() {
        this.fetchLocations();
    },
};
</script>

<style lang="scss" scoped>
.tab-detail {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .header-actions {
            display: flex;
            gap: 10px;
        }
    }

    .locations-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        gap: 24px;
        margin-top: 15px;

        .location-card {
            background: var(--color-bg-subtle);
            border-radius: 12px;
            padding: 20px;
            display: flex;
            gap: 16px;
            transition: transform 0.2s, box-shadow 0.2s;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            }

            .location-icon {
                width: 60px;
                height: 60px;
                background: #4285f4;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;

                .material-icons {
                    font-size: 28px;
                    color: #fff;
                }
            }

            .info {
                flex: 1;
                min-width: 0;

                .location-name {
                    margin: 0 0 8px;
                    font-size: 16px;
                    font-weight: 600;
                    color: var(--color-text-strong-default);
                }

                .location-address {
                    display: flex;
                    align-items: flex-start;
                    gap: 4px;
                    font-size: 13px;
                    color: var(--color-text-default);
                    margin-bottom: 12px;

                    .material-icons {
                        font-size: 16px;
                        flex-shrink: 0;
                    }
                }

                .meta {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                    color: var(--color-text-default);
                    font-size: 13px;

                    span {
                        display: flex;
                        align-items: center;
                        gap: 4px;

                        .material-icons {
                            font-size: 16px;
                            color: #fbbc05;
                        }
                    }
                }
            }
        }
    }

    .loading,
    .empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }

    .pagination {
        display: flex;
        justify-content: center;
        margin-top: 24px;
    }
}
</style>
